import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, type SerpApiLoggedUsageView, type SerpApiQuotaView } from "../../lib/api";

/**
 * "Enrich top N" for the autopilot row.
 *
 * The autopilot only writes enriched keywords, and nothing enriches on a
 * schedule — so the queue silently runs dry. This fills it in the autopilot's
 * own rank order, without moving anything out of `backlog`.
 */
export function EnrichQueuePanel() {
  const qc = useQueryClient();
  const [limit, setLimit] = useState(100);
  const [note, setNote] = useState<string | null>(null);
  const status = useQuery({
    queryKey: ["automations", "enrich-queue"],
    queryFn: () => api.getEnrichQueue(),
    refetchInterval: (q) => (q.state.data?.running ? 5_000 : false),
  });
  const running = Boolean(status.data?.running);
  // SerpAPI is quota-limited (Free Plan), so what bounds a run is searches left,
  // not dollars. Re-read while a run is spending them.
  const quota = useQuery({
    queryKey: ["automations", "serpapi-quota"],
    queryFn: () => api.getSerpApiQuota(),
    refetchInterval: running ? 30_000 : false,
  });
  const start = useMutation({
    mutationFn: () => api.startEnrichQueue(limit),
    onSuccess: (r) => {
      setNote(r.started ? null : r.message ?? "Nothing to enrich.");
      void qc.invalidateQueries({ queryKey: ["automations", "enrich-queue"] });
      void qc.invalidateQueries({ queryKey: ["automations", "serpapi-quota"] });
    },
    onError: (e: Error) => setNote(e.message),
  });

  const job = status.data?.job ?? null;
  const count = (o: string) => job?.items.filter((i) => i.outcome === o).length ?? 0;
  const q = quota.data?.quota ?? null;
  const failedKeys = q?.keys.filter((k) => !k.ok).map((k) => k.key) ?? [];

  return (
    <div className="mt-3 rounded border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-700">
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-medium">Enrich queue</span>
        <span className="text-slate-500">Top</span>
        <input
          type="number"
          min={1}
          max={800}
          value={limit}
          onChange={(e) => setLimit(Math.max(1, Math.min(800, Number(e.target.value) || 1)))}
          className="h-6 w-16 rounded border border-slate-300 px-1 text-xs"
        />
        <span className="text-slate-500">
          not-enriched candidates ·{" "}
          {quota.isLoading
            ? "checking SerpAPI quota…"
            : quota.isError
              ? "SerpAPI quota unavailable"
              : q
                ? `SerpAPI: ${q.totalSearchesLeft} searches left (~${q.enrichesLeft} keywords)`
                : null}
        </span>
        {q && limit > q.enrichesLeft && (
          <span className="text-amber-700">quota covers {q.enrichesLeft} of {limit}; the run pauses when it runs out</span>
        )}
        {failedKeys.length > 0 && <span className="text-amber-700">could not read {failedKeys.join(", ")}</span>}
        {quota.data?.logged && <LoggedUsage q={q} logged={quota.data.logged} />}
        <button
          type="button"
          disabled={running || start.isPending}
          onClick={() => start.mutate()}
          className="rounded border border-slate-300 bg-white px-2 py-1 font-medium hover:bg-slate-50 disabled:opacity-50"
        >
          {running ? `Enriching… ${job?.done ?? 0}/${job?.total ?? 0}` : "Enrich"}
        </button>
      </div>
      {note && <div className="mt-1 text-amber-700">{note}</div>}
      {job && (
        <div className="mt-1 text-slate-600">
          {job.status === "running" ? "Running" : job.status === "done" ? "Last run done" : "Last run stopped"} ·{" "}
          {count("enriched")} enriched · {count("already-enriched")} already had data · {count("no-competitors")} no usable
          competitors · {count("error")} failed · ${job.estimatedCostUsd.toFixed(2)} logged
          {job.current && <> · now: <span dir="rtl">{job.current}</span></>}
          {job.error && <div className="text-red-700">{job.error}</div>}
        </div>
      )}
    </div>
  );
}

/**
 * The account's own count next to what the SerpAPI client logged
 * (serpapi_calls). A gap means searches that did not go through the client
 * since logging started (e.g. on 2026-10-15 a script spent ~154 that no screen
 * showed), or usage from earlier in the month, before logging existed.
 */
function LoggedUsage({ q, logged }: { q: SerpApiQuotaView | null; logged: SerpApiLoggedUsageView }) {
  const accountUsed = (q?.keys ?? []).reduce((s, k) => s + (k.ok ? k.thisMonthUsage ?? 0 : 0), 0);
  const gap = accountUsed - logged.billable;
  const since = logged.since ? new Date(logged.since).toLocaleDateString("he-IL") : null;
  return (
    <span className="text-slate-500" title={logged.byCaller.map((c) => `${c.caller}: ${c.billable}`).join("\n")}>
      · client logged {logged.billable} billable this month{since ? ` (since ${since})` : ""}
      {gap > 10 && <span className="text-amber-700"> · account shows {accountUsed}: {gap} not through the client</span>}
    </span>
  );
}
